/**
 * Asset Summary Panel — data table with tab filtering, total selected value,
 * group-by-type subtotals, and a detail panel that slides in from the right.
 *
 * Reads canonical asset entries from the `assetEntries` table.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Switch } from "@/ui/primitives/switch";
import { BulkActionBar, BulkActionButton } from "@/ui/composed/bulk-action-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  Package,
  ChevronRight,
  ChevronDown,
  Plus,
  AlertTriangle,
  Eye,
  EyeOff,
  ChevronsUpDown,
  RefreshCw,
  Unlink,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { usePersistedSet } from "@/utils/use-persisted-set";
import type { AssetEntryUI } from "../types";
import {
  formatFullCurrency,
  formatSourceType,
  getSelectedValue,
  getBestValue,
  getRocapValue,
  getPractitionerValue,
  getLowRecovery,
  getHighRecovery,
  collectValuationSources,
  getValuationForSource,
  filterAssets,
  computeSecurityLinkStatusByAsset,
  SECURITY_LINK_WARNING,
  isHiddenValuationSource,
  type AssetSecurityLinkStatus,
} from "../helpers";
import {
  computeSecuredNetting,
  netOfSecurity,
  type NettingLiability,
  type NettingLink,
  type NettingSecurityInterest,
} from "../securedNetting";
import {
  ASSET_TYPE_LABEL,
  ASSET_TYPE_BADGE_COLOR,
  FILTER_TABS,
  type FilterTab,
} from "../constants";
import { directorDisclosureLabel } from "@/lib/director-disclosure";
import { BulkValuationDialog } from "../../shared-panels/BulkValuationDialog";
import { AssetPanel } from "./AssetPanel";
import { ValuationColumnsMenu } from "./ValuationColumnsMenu";
import type { SecuredCalcBreakdownUI } from "./NetValue";
import { AddAssetPanel } from "./AddAssetPanel";
import {
  useFlagDismissals,
  flagGlowClass,
  type UseFlagDismissalsResult,
} from "../../flags";
import {
  useLinkCounts,
  useLinkedRecordStack,
} from "../../shared-panels/linkShared";
import { LinkIndicator } from "../../shared-panels/LinkIndicator";
import { SecondaryDetailPanel } from "../../shared-panels/SecondaryDetailPanel";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../../_shared/new-output-badge";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The value-column subtotals shown per group / grand total. */
interface RecoveryTotals {
  rocap: number;
  practitioner: number;
  low: number;
  high: number;
}

interface AssetGroup {
  /** Stable key for collapse/expand state (`null` = no group header). */
  key: string | null;
  /** Human-readable group label (e.g. "Plant and Equipment" or "Secured"). */
  label: string;
  entries: AssetEntryUI[];
  /** Underlying selected/best value sum — used only to order the groups. */
  sortValue: number;
  /** The three displayed scenario subtotals (ROCAP / Low / High). */
  totals: RecoveryTotals;
  /** Per-visible-valuation-source sums, ordered like the visible columns. */
  valuationTotals: number[];
}

/** Grouping dimension for the Asset Summary table. */
type GroupBy = "type" | "secured";

/** Per-asset value selectors, one per scenario column (gross or net-of-security). */
interface ValueSelectors {
  rocap: (e: AssetEntryUI) => number | null;
  practitioner: (e: AssetEntryUI) => number | null;
  low: (e: AssetEntryUI) => number | null;
  high: (e: AssetEntryUI) => number | null;
}

/** Sum a nullable per-asset selector across rows, skipping excluded assets. */
function sumScenario(
  rows: AssetEntryUI[],
  selector: (e: AssetEntryUI) => number | null,
): number {
  return rows.reduce((s, e) => (e.isExcluded ? s : s + (selector(e) ?? 0)), 0);
}

/** Compute the value-column subtotals for a set of rows. */
function recoveryTotals(
  rows: AssetEntryUI[],
  selectors: ValueSelectors,
): RecoveryTotals {
  return {
    rocap: sumScenario(rows, selectors.rocap),
    practitioner: sumScenario(rows, selectors.practitioner),
    low: sumScenario(rows, selectors.low),
    high: sumScenario(rows, selectors.high),
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AssetListPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the "Total Value" header and the "Add Asset"
   * button so the panel renders as table-only. Used by the Overview
   * (Plan) page where each section already has its own chrome and the
   * cards are duplicative.
   */
  compact?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<FilterTab>("All Assets");
  const [groupBy, setGroupBy] = useState<GroupBy>("type");
  // Default to netting each asset off its security (residual / "true" value).
  const [netOffSecurity, setNetOffSecurity] = useState(true);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [showAddAsset, setShowAddAsset] = useState(false);
  // Bulk multi-select (checkbox column). Scoped to the current tab — cleared
  // whenever the tab changes so an Active selection can't leak into Excluded.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkValuationOpen, setBulkValuationOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const bulkToggleExcluded = useMutation(
    api.jobs.summarization.agents.assetList.publicMutations.bulkToggleExcluded,
  );
  const bulkAddValuation = useMutation(
    api.jobs.summarization.agents.assetList.publicMutations.bulkAddValuation,
  );
  // MOR-626 — groups collapsed by default; the user's expand/collapse choice
  // persists globally to localStorage (shared with the overview view).
  const [expandedGroups, setExpandedGroups] = usePersistedSet(
    "assets:expanded-groups",
  );
  // Local bridge: true from the moment the button is clicked until the recovery
  // task is observed as running (covers the brief schedule→start gap so the
  // button can't be re-clicked in that window).
  const [reevaluating, setReevaluating] = useState(false);

  const rerunAssetRecovery = useAction(
    api.jobs.summarization.actions.rerunAssetRecovery,
  );

  // Live status of the Asset Recovery agent task for this job. Drives the
  // disabled state so the button stays locked for the whole background run —
  // including runs kicked off by the debounce after edits, not just this button.
  const { data: summarizationTasks } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      {
        jobId,
      },
    ),
  );
  const recoveryRunning = (summarizationTasks ?? []).some(
    (t) =>
      t.agentId === "sum_asset_recovery" &&
      (t.status === "running" || t.status === "waiting"),
  );
  // Hand off from the local bridge to the query-driven state once the run is
  // observed as in-flight.
  useEffect(() => {
    if (recoveryRunning && reevaluating) setReevaluating(false);
  }, [recoveryRunning, reevaluating]);
  const recoveryBusy = reevaluating || recoveryRunning;

  const flagCtx = useFlagDismissals(jobId);
  const linkCounts = useLinkCounts(jobId);
  const newSince = useNewOutputBoundary(jobId, "sum_asset_list");

  // Stack of side-by-side linked-record panels so the user can chase a chain
  // of related records (Asset → SI → Creditor → Loan ...) without losing the
  // primary panel.
  const linkedStack = useLinkedRecordStack(
    selectedEntryId ? { type: "asset", id: selectedEntryId } : null,
  );

  const { data: allEntries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAssetEntriesForJob, {
      jobId,
      includesMerged: true,
    }),
  );

  const entries = useMemo(() => allEntries ?? [], [allEntries]);

  const activeEntries = useMemo(
    () => entries.filter((e) => e.status === "active"),
    [entries],
  );

  // MOR-544 — shared per-job view prefs (valuation columns + overview
  // category toggles). Read from the job doc so every member sees the same
  // curated view; react-query dedupes this against the page's own getJob.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const viewPrefs = job?.assetViewPrefs;
  const updateViewPrefs = useMutation(api.jobs.mutations.updateAssetViewPrefs);

  // Director-disclosure label for the job's appointment type (ROCAP for
  // corporate insolvencies, SOA for bankruptcies, etc.). Purely a display
  // concern — the underlying figure is resolved identically for every type.
  const disclosure = directorDisclosureLabel(job?.appointmentType);

  // Every distinct valuation source on the job — the "Columns" menu list.
  // The raw "ROCAP" and "Director Estimate" sources are excluded: the fixed
  // ROCAP column already shows the resolved director figure (a director's
  // annexure estimate is resolved into ROCAP), so separate raw breakdown
  // columns are redundant and read as a contradictory second director total.
  const availableSources = useMemo(
    () =>
      collectValuationSources(activeEntries).filter(
        (source) => !isHiddenValuationSource(source),
      ),
    [activeEntries],
  );

  // Stored prefs intersected with what actually exists, so a stale stored
  // source (e.g. all its valuations were deleted) is ignored harmlessly.
  const visibleValuationColumns = useMemo(
    () =>
      (viewPrefs?.visibleValuationColumns ?? []).filter((s) =>
        availableSources.includes(s),
      ),
    [viewPrefs?.visibleValuationColumns, availableSources],
  );

  const toggleValuationColumn = (sourceType: string) => {
    const stored = viewPrefs?.visibleValuationColumns ?? [];
    // Prune stale sources on write so the stored list tracks reality.
    const live = stored.filter((s) => availableSources.includes(s));
    const next = live.includes(sourceType)
      ? live.filter((s) => s !== sourceType)
      : [...live, sourceType];
    void updateViewPrefs({ jobId, visibleValuationColumns: next });
  };

  // Overview-only display toggles (compact mode). Hiding is display-only:
  // grandTotals/waterfall figures are computed from `filtered`, never from
  // the visible group list, so hiding a category cannot change any number.
  const overviewShowAssets = viewPrefs?.overviewShowAssets ?? true;
  const overviewHiddenGroups = useMemo(
    () => new Set(viewPrefs?.overviewHiddenGroups ?? []),
    [viewPrefs?.overviewHiddenGroups],
  );

  const toggleOverviewGroup = (key: string) => {
    const hidden = viewPrefs?.overviewHiddenGroups ?? [];
    const next = hidden.includes(key)
      ? hidden.filter((k) => k !== key)
      : [...hidden, key];
    void updateViewPrefs({ jobId, overviewHiddenGroups: next });
  };

  const toggleOverviewShowAssets = () => {
    void updateViewPrefs({ jobId, overviewShowAssets: !overviewShowAssets });
  };

  // Creditors + record links power the secured-netting display (residual / "true
  // value" of an encumbered asset). Reuses the same canonical queries the
  // Estimated Outcome Waterfall reads, so the figures stay consistent.
  const { data: liabilityEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId },
    ),
  );
  const { data: recordLinks } = useQuery(
    convexQuery(
      api.jobs.summarization.recordLinks.linkQueries.listLinksForJob,
      { jobId },
    ),
  );
  // Security interests carry their own owed figure (PoD / document / override),
  // which the netting uses as the canonical debt — "what you see is netted".
  const { data: securityInterestEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listSecurityInterestEntriesForJob,
      { jobId },
    ),
  );

  // Secured netting. We compute ONE headline apportionment (each asset's fixed
  // share of the security registered against it) and net every valuation column
  // against that same per-asset figure. `securedByAsset` drives the per-row
  // Secured badge, the column nets, and the detail-panel "Net" popover — one
  // deduction everywhere, so the table can never disagree with the popover.
  // (Re-apportioning per column would let a co-asset that's worth $0 in one
  // scenario dump its share of the debt onto its siblings, zeroing out an asset
  // the popover still shows a positive net for.)
  const netting = useMemo(() => {
    const liabs = (liabilityEntries ?? []) as NettingLiability[];
    const links = (recordLinks ?? []) as NettingLink[];
    const sis = (securityInterestEntries ?? []) as NettingSecurityInterest[];
    const headline = computeSecuredNetting(
      activeEntries,
      liabs,
      sis,
      links,
      (e) => getSelectedValue(e) ?? getBestValue(e),
    );
    return {
      securedByAsset: headline.securedByAsset,
      breakdownByAsset: headline.breakdownByAsset,
      // Per-asset reason a secured asset can't show a net value yet (no SI
      // linked, or the SI has no owed amount), powering the broken-link warning.
      linkStatusByAsset: computeSecurityLinkStatusByAsset(
        activeEntries,
        headline,
      ),
    };
  }, [activeEntries, liabilityEntries, securityInterestEntries, recordLinks]);
  const securedByAsset = netting.securedByAsset;
  const linkStatusByAsset = netting.linkStatusByAsset;

  // Resolve the headline netting breakdown (ids only) into label-bearing rows the
  // Asset panel's NetValue popover can render: SI name (+ PPSR tag) and creditor
  // names, pulled from the already-loaded SI/creditor queries.
  const breakdownUIByAsset = useMemo(() => {
    const siById = new Map<string, { name: string; tag?: string }>();
    for (const si of (securityInterestEntries ?? []) as Array<{
      _id: string;
      securedParty?: { name?: string };
      ppsrRegistrationNumber?: string;
    }>) {
      siById.set(si._id, {
        name: si.securedParty?.name ?? "Unnamed secured party",
        tag: si.ppsrRegistrationNumber
          ? `PPSR #${si.ppsrRegistrationNumber}`
          : "Unperfected",
      });
    }
    const creditorById = new Map<string, string>();
    for (const c of (liabilityEntries ?? []) as Array<{
      _id: string;
      creditorName?: string;
    }>) {
      creditorById.set(c._id, c.creditorName ?? "Creditor");
    }

    const out = new Map<string, SecuredCalcBreakdownUI>();
    for (const [assetId, bd] of netting.breakdownByAsset) {
      out.set(assetId, {
        totalDeduction: bd.totalDeduction,
        securityInterests: bd.sis.map((si) => {
          const meta = siById.get(si.siId);
          return {
            siId: si.siId,
            label: meta?.name ?? "Security interest",
            tag: meta?.tag,
            siDeduction: si.siDeduction,
            declaredOverride: si.declaredOverride,
            creditors: si.creditors.map((c) => ({
              id: c.creditorId,
              label: creditorById.get(c.creditorId) ?? "Creditor",
              amount: c.amount,
            })),
          };
        }),
      });
    }
    return out;
  }, [netting.breakdownByAsset, securityInterestEntries, liabilityEntries]);

  // Active value selectors for the four columns. In net mode an encumbered
  // asset's column shows its value less the FIXED per-asset secured deduction
  // (`securedByAsset`) — the same figure the detail-panel "Net" popover uses, so
  // the two always agree. Unencumbered assets and blank valuations pass through
  // unchanged.
  const valueSelectors = useMemo<ValueSelectors>(() => {
    if (!netOffSecurity) {
      return {
        rocap: getRocapValue,
        practitioner: getPractitionerValue,
        low: getLowRecovery,
        high: getHighRecovery,
      };
    }
    const mk =
      (sel: (e: AssetEntryUI) => number | null) =>
      (e: AssetEntryUI): number | null =>
        netOfSecurity(sel(e), netting.securedByAsset.get(e._id) ?? 0);
    return {
      rocap: mk(getRocapValue),
      practitioner: mk(getPractitionerValue),
      low: mk(getLowRecovery),
      high: mk(getHighRecovery),
    };
  }, [netOffSecurity, netting]);

  // Per-source column selectors — net-of-security exactly like the fixed
  // columns so a Balance Sheet column stays comparable with ROCAP et al.
  const valuationColumnSelectors = useMemo(() => {
    const map = new Map<string, (e: AssetEntryUI) => number | null>();
    for (const source of visibleValuationColumns) {
      map.set(source, (e: AssetEntryUI) => {
        const gross = getValuationForSource(e, source);
        if (!netOffSecurity) return gross;
        return netOfSecurity(gross, netting.securedByAsset.get(e._id) ?? 0);
      });
    }
    return map;
  }, [visibleValuationColumns, netOffSecurity, netting]);

  /** Ordered per-source sums for a set of rows (missing counts as 0). */
  const valuationTotalsFor = useCallback(
    (rows: AssetEntryUI[]): number[] =>
      visibleValuationColumns.map((source) => {
        const selector = valuationColumnSelectors.get(source);
        return selector ? sumScenario(rows, selector) : 0;
      }),
    [visibleValuationColumns, valuationColumnSelectors],
  );

  const filtered = useMemo(
    () => filterAssets(activeEntries, activeTab),
    [activeEntries, activeTab],
  );

  // Grouping is only meaningful on the "All Assets" tab — every other tab
  // already narrows the rows down. When grouping by "secured" but the user
  // has tabbed onto Secured / Unsecured the dimension would collapse to one
  // header so we render flat in that case (same code path as `!isGrouped`).
  const isGrouped = activeTab === "All Assets" && filtered.length > 0;

  // -------------------------------------------------------------------------
  // Sort entries: secured-first (subjectToSecurityInterest === true), then by
  // selected/best value descending. This keeps every asset that's collateral
  // for a security interest at the top of its group, matching the parallel
  // sort applied to the Creditors panel.
  // -------------------------------------------------------------------------

  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aSecured = a.subjectToSecurityInterest ? 1 : 0;
      const bSecured = b.subjectToSecurityInterest ? 1 : 0;
      if (aSecured !== bSecured) return bSecured - aSecured;
      const aVal = getSelectedValue(a) ?? getBestValue(a);
      const bVal = getSelectedValue(b) ?? getBestValue(b);
      return bVal - aVal;
    });
  }, [filtered]);

  // -------------------------------------------------------------------------
  // Bulk selection helpers (checkbox column + floating action bar)
  // -------------------------------------------------------------------------

  // Selection is scoped to whatever tab you're on, so clear it on tab change.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const selectionEnabled = !isViewer;
  const visibleIds = useMemo(
    () => sortedFiltered.map((e) => e._id),
    [sortedFiltered],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  // On the "Excluded" tab the selection is already excluded → the action
  // re-includes; everywhere else it excludes.
  const bulkExcludeIntent = activeTab !== "Excluded";

  const handleBulkExclude = useCallback(async () => {
    const ids = [...selectedIds] as Id<"assetEntries">[];
    if (ids.length === 0) return;
    try {
      const res = await bulkToggleExcluded({
        entryIds: ids,
        isExcluded: bulkExcludeIntent,
      });
      toast.success(
        `${bulkExcludeIntent ? "Excluded" : "Re-included"} ${res.updated} asset${
          res.updated === 1 ? "" : "s"
        }`,
      );
      setSelectedIds(new Set());
    } catch {
      toast.error("Couldn't update the selected assets. Please try again.");
    }
  }, [selectedIds, bulkExcludeIntent, bulkToggleExcluded]);

  const handleBulkValuation = useCallback(
    async (input: { amount: number; asAtDate?: string; notes?: string }) => {
      const ids = [...selectedIds] as Id<"assetEntries">[];
      if (ids.length === 0) return;
      setBulkSubmitting(true);
      try {
        const res = await bulkAddValuation({
          entryIds: ids,
          value: input.amount,
          asAtDate: input.asAtDate,
          notes: input.notes,
        });
        toast.success(
          `Updated valuations on ${res.updated} asset${
            res.updated === 1 ? "" : "s"
          }`,
        );
        setBulkValuationOpen(false);
        setSelectedIds(new Set());
      } catch {
        toast.error("Couldn't update the valuations. Please try again.");
      } finally {
        setBulkSubmitting(false);
      }
    },
    [selectedIds, bulkAddValuation],
  );

  // -------------------------------------------------------------------------
  // Group entries by asset type (All Assets tab) or show flat list
  // -------------------------------------------------------------------------

  const groups: AssetGroup[] = useMemo(() => {
    if (!isGrouped) {
      return [
        {
          key: null,
          label: "",
          entries: sortedFiltered,
          sortValue: 0,
          totals: { rocap: 0, practitioner: 0, low: 0, high: 0 },
          valuationTotals: valuationTotalsFor(sortedFiltered),
        },
      ];
    }

    const sortTotal = (rows: AssetEntryUI[]) =>
      sumScenario(rows, (e) => getSelectedValue(e) ?? getBestValue(e));

    if (groupBy === "secured") {
      const securedRows = sortedFiltered.filter(
        (e) => e.subjectToSecurityInterest,
      );
      const unsecuredRows = sortedFiltered.filter(
        (e) => !e.subjectToSecurityInterest,
      );
      const out: AssetGroup[] = [];
      if (securedRows.length > 0) {
        out.push({
          key: "secured",
          label: "Secured",
          entries: securedRows,
          sortValue: sortTotal(securedRows),
          totals: recoveryTotals(securedRows, valueSelectors),
          valuationTotals: valuationTotalsFor(securedRows),
        });
      }
      if (unsecuredRows.length > 0) {
        out.push({
          key: "unsecured",
          label: "Unsecured",
          entries: unsecuredRows,
          sortValue: sortTotal(unsecuredRows),
          totals: recoveryTotals(unsecuredRows, valueSelectors),
          valuationTotals: valuationTotalsFor(unsecuredRows),
        });
      }
      return out;
    }

    const map = new Map<string, AssetEntryUI[]>();
    for (const entry of sortedFiltered) {
      const key = entry.assetType;
      const bucket = map.get(key) ?? [];
      map.set(key, bucket);
      bucket.push(entry);
    }

    return Array.from(map.entries())
      .map(([type, groupEntries]) => ({
        key: type,
        label: ASSET_TYPE_LABEL[type] ?? type,
        entries: groupEntries,
        sortValue: sortTotal(groupEntries),
        totals: recoveryTotals(groupEntries, valueSelectors),
        valuationTotals: valuationTotalsFor(groupEntries),
      }))
      .sort((a, b) => b.sortValue - a.sortValue);
  }, [sortedFiltered, isGrouped, groupBy, valueSelectors, valuationTotalsFor]);

  // Groups actually rendered in the table body. Only the Overview filters.
  const displayGroups =
    compact && isGrouped
      ? groups.filter((g) => g.key === null || !overviewHiddenGroups.has(g.key))
      : groups;

  // -------------------------------------------------------------------------
  // Totals
  // -------------------------------------------------------------------------

  const grandTotals = useMemo(
    () => recoveryTotals(filtered, valueSelectors),
    [filtered, valueSelectors],
  );

  const grandValuationTotals = useMemo(
    () => valuationTotalsFor(filtered),
    [filtered, valuationTotalsFor],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  // -------------------------------------------------------------------------
  // Collapse / expand helpers
  // -------------------------------------------------------------------------

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const allGroupTypes = useMemo(
    () => groups.map((g) => g.key).filter((key): key is string => key !== null),
    [groups],
  );

  // Seed expand state on first render and re-seed when the set of group keys
  // changes (e.g. when the user switches "Group by" between Type and Secured).
  // Without re-seeding, the headers from the previous grouping stay collapsed
  // and the new headers from a different dimension are also collapsed.
  const seededKeySigRef = useRef<string>("");
  useEffect(() => {
    const sig = allGroupTypes.join("|");
    if (sig !== seededKeySigRef.current && allGroupTypes.length > 0) {
      setExpandedGroups(new Set(allGroupTypes));
      seededKeySigRef.current = sig;
    }
  }, [allGroupTypes, setExpandedGroups]);

  const allExpanded =
    allGroupTypes.length > 0 &&
    allGroupTypes.every((t) => expandedGroups.has(t));
  const noneExpanded =
    isGrouped &&
    allGroupTypes.length > 0 &&
    !allGroupTypes.some((t) => expandedGroups.has(t));

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(allGroupTypes));
    }
  };

  // -------------------------------------------------------------------------
  // Column span helpers
  // -------------------------------------------------------------------------

  const showDetailColumns = !noneExpanded;
  const fixedColCount = isGrouped ? (showDetailColumns ? 4 : 1) : 5;
  // fixed columns + four value columns (ROCAP / Practitioner / Low / High)
  // + N valuation-source columns + chevron.
  const totalColCount = fixedColCount + 4 + visibleValuationColumns.length + 1;

  // -------------------------------------------------------------------------
  // Empty / loading states
  // -------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">Extracting assets...</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <Package className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No asset entries yet.</p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header — Total Selected Value + Add Asset (hidden in compact mode) */}
        {!compact && (
          <div className="flex items-start justify-between px-5 pb-4 pt-5">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Asset Totals
              </p>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <div>
                  <span className="mr-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {disclosure.short}
                  </span>
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatFullCurrency(grandTotals.rocap)}
                  </span>
                </div>
                <div>
                  <span className="mr-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Practitioner
                  </span>
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatFullCurrency(grandTotals.practitioner)}
                  </span>
                </div>
                <div>
                  <span className="mr-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Low
                  </span>
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatFullCurrency(grandTotals.low)}
                  </span>
                </div>
                <div>
                  <span className="mr-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    High
                  </span>
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatFullCurrency(grandTotals.high)}
                  </span>
                </div>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {filtered.length} asset{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>
            {!isViewer && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={recoveryBusy}
                  onClick={async () => {
                    if (recoveryBusy) return;
                    setReevaluating(true);
                    // Safety net: if the run no-ops instantly (e.g. no assets)
                    // and we never observe a "running" status, release the
                    // local bridge after a few seconds.
                    const release = window.setTimeout(
                      () => setReevaluating(false),
                      8000,
                    );
                    try {
                      await rerunAssetRecovery({ jobId });
                    } catch {
                      window.clearTimeout(release);
                      setReevaluating(false);
                    }
                  }}
                  className="gap-1.5"
                  title="Run the AI estimate of realisable value for every asset"
                >
                  <RefreshCw
                    className={cn(
                      "h-3.5 w-3.5",
                      recoveryBusy && "animate-spin",
                    )}
                  />
                  {recoveryBusy ? "Running AI estimate…" : "Run AI estimate"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowAddAsset(true)}
                  className="gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Asset
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Tab bar */}
        <div className="scrollbar-none flex items-center gap-1 overflow-x-auto border-b border-border px-5">
          {FILTER_TABS.map((tab) => {
            const count = filterAssets(activeEntries, tab).length;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                  activeTab === tab
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {tab}
                <span
                  className={cn(
                    "ml-1.5 text-xs tabular-nums",
                    activeTab === tab
                      ? "text-muted-foreground"
                      : "text-muted-foreground/50",
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-3 whitespace-nowrap">
            {/* MOR-544 — per-source valuation columns, shared across the job. */}
            <ValuationColumnsMenu
              sources={availableSources}
              visibleSources={visibleValuationColumns}
              onToggle={toggleValuationColumn}
              disabled={isViewer}
            />
            {/* Net-off-security toggle — nets each asset against the security
                registered against it across every valuation column. */}
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-xs text-muted-foreground">
              <Switch
                checked={netOffSecurity}
                onCheckedChange={setNetOffSecurity}
                aria-label="Net off security interests"
              />
              <span className="font-medium uppercase tracking-wider">
                Net off security
              </span>
            </label>
            {isGrouped && (
              <>
                {/* Group-by toggle — only meaningful when grouping is on. */}
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="font-medium uppercase tracking-wider">
                    Group by
                  </span>
                  <Select
                    value={groupBy}
                    onValueChange={(value) => setGroupBy(value as GroupBy)}
                  >
                    <SelectTrigger
                      className="h-7 w-auto gap-1 px-2 text-xs"
                      aria-label="Group by"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="end">
                      <SelectItem value="type">Type</SelectItem>
                      <SelectItem value="secured">Secured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="flex items-center gap-1 whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronsUpDown className="h-3 w-3" />
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Overview-only: display toggles for the whole asset list and each
            category. Eye chips mirror the Columns menu affordance. Hiding is
            display-only — totals and the outcome waterfall are unchanged. */}
        {compact && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-5 py-2">
            <button
              type="button"
              disabled={isViewer}
              onClick={toggleOverviewShowAssets}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                overviewShowAssets
                  ? "border-border/60 bg-background text-foreground hover:bg-secondary"
                  : "border-dashed border-border text-muted-foreground hover:text-foreground",
                isViewer && "cursor-not-allowed opacity-60",
              )}
            >
              {overviewShowAssets ? (
                <Eye className="size-3.5" />
              ) : (
                <EyeOff className="size-3.5" />
              )}
              All assets
            </button>
            {overviewShowAssets &&
              isGrouped &&
              groups
                .flatMap((g) =>
                  g.key === null ? [] : [{ key: g.key, label: g.label }],
                )
                .map(({ key, label }) => {
                  const hidden = overviewHiddenGroups.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={isViewer}
                      onClick={() => toggleOverviewGroup(key)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition",
                        hidden
                          ? "border-dashed border-border text-muted-foreground hover:text-foreground"
                          : "border-border/60 bg-background text-foreground hover:bg-secondary",
                        isViewer && "cursor-not-allowed opacity-60",
                      )}
                    >
                      {hidden ? (
                        <EyeOff className="size-3.5" />
                      ) : (
                        <Eye className="size-3.5" />
                      )}
                      {label}
                    </button>
                  );
                })}
          </div>
        )}

        {/* Data table — hidden on the Overview when "All assets" is off. */}
        {compact && !overviewShowAssets ? (
          <p className="px-5 py-6 text-center text-xs text-muted-foreground">
            Asset list hidden. Use the All assets toggle to show it.
          </p>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="max-h-[calc(100vh-460px)] overflow-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-transparent">
                      <TableHead className="sticky left-0 z-10 h-10 whitespace-nowrap bg-muted/30 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <div className="flex items-center gap-2.5">
                          {selectionEnabled && (
                            <Checkbox
                              aria-label="Select all assets"
                              checked={
                                allVisibleSelected
                                  ? true
                                  : someVisibleSelected
                                    ? "indeterminate"
                                    : false
                              }
                              onCheckedChange={toggleSelectAll}
                            />
                          )}
                          Description
                        </div>
                      </TableHead>
                      {!isGrouped && (
                        <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Type
                        </TableHead>
                      )}
                      {showDetailColumns && (
                        <>
                          <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Status
                          </TableHead>
                          <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Secured
                          </TableHead>
                          <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Flags
                          </TableHead>
                        </>
                      )}
                      <TableHead className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {disclosure.short}
                      </TableHead>
                      <TableHead className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Practitioner
                      </TableHead>
                      <TableHead className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Low Est.
                      </TableHead>
                      <TableHead className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        High Est.
                      </TableHead>
                      {visibleValuationColumns.map((source) => (
                        <TableHead
                          key={source}
                          className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                        >
                          {formatSourceType(source)}
                        </TableHead>
                      ))}
                      <TableHead className="h-10 w-10" />
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {sortedFiltered.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={totalColCount}
                          className="h-32 text-center"
                        >
                          <span className="text-sm text-muted-foreground">
                            No assets match this filter.
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayGroups.map((group) => {
                        const groupKey = group.key;
                        const isExpanded =
                          !isGrouped ||
                          !groupKey ||
                          expandedGroups.has(groupKey);
                        return (
                          <Fragment key={groupKey ?? "ungrouped"}>
                            {/* Group header row */}
                            {isGrouped && groupKey && (
                              <GroupHeaderRow
                                group={group}
                                fixedColCount={fixedColCount}
                                isExpanded={isExpanded}
                                onToggle={() => toggleGroup(groupKey)}
                              />
                            )}

                            {/* Entry rows (hidden when collapsed) */}
                            {isExpanded &&
                              group.entries.map((entry) => (
                                <EntryRow
                                  key={entry._id}
                                  entry={entry}
                                  isNew={isNewOutput(
                                    entry._creationTime,
                                    newSince,
                                  )}
                                  isSelected={selectedEntryId === entry._id}
                                  isGrouped={isGrouped}
                                  onSelect={() => setSelectedEntryId(entry._id)}
                                  selectionEnabled={selectionEnabled}
                                  isChecked={selectedIds.has(entry._id)}
                                  onToggleCheck={() => toggleSelect(entry._id)}
                                  flagCtx={flagCtx}
                                  linkCount={
                                    linkCounts.byLinkedEntity.get(entry._id) ??
                                    0
                                  }
                                  rocapValue={valueSelectors.rocap(entry)}
                                  practitionerValue={valueSelectors.practitioner(
                                    entry,
                                  )}
                                  lowValue={valueSelectors.low(entry)}
                                  highValue={valueSelectors.high(entry)}
                                  valuationValues={visibleValuationColumns.map(
                                    (source) =>
                                      valuationColumnSelectors.get(source)?.(
                                        entry,
                                      ) ?? null,
                                  )}
                                  securedBalance={securedByAsset.get(entry._id)}
                                  linkStatus={linkStatusByAsset.get(entry._id)}
                                />
                              ))}
                          </Fragment>
                        );
                      })
                    )}

                    {/* Grand total row */}
                    {sortedFiltered.length > 0 && (
                      <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                        <TableCell
                          colSpan={fixedColCount}
                          className="px-4 py-3 text-sm font-bold text-muted-foreground"
                        >
                          TOTAL
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatFullCurrency(grandTotals.rocap)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatFullCurrency(grandTotals.practitioner)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatFullCurrency(grandTotals.low)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatFullCurrency(grandTotals.high)}
                          </span>
                        </TableCell>
                        {grandValuationTotals.map((total, i) => (
                          <TableCell key={i} className="px-4 py-3 text-right">
                            <span className="text-sm font-bold tabular-nums text-foreground">
                              {formatFullCurrency(total)}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Footer hint */}
            <div className="border-t border-border px-5 py-3">
              <p className="text-center text-xs text-muted-foreground">
                Click any row to view valuation sources, select a value, or add
                your own
              </p>
            </div>
          </>
        )}
      </div>

      {/* Detail panel */}
      <AssetPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        isViewer={isViewer}
        flagCtx={flagCtx}
        onOpenLinked={linkedStack.open}
        securedBreakdown={
          selectedEntry ? breakdownUIByAsset.get(selectedEntry._id) : undefined
        }
        securityLinkStatus={
          selectedEntry ? linkStatusByAsset.get(selectedEntry._id) : undefined
        }
      />

      {/* Stack of side-by-side linked records */}
      <SecondaryDetailPanel
        jobId={jobId}
        stack={linkedStack.stack}
        onCloseAt={linkedStack.closeAt}
        onOpenLinked={linkedStack.open}
        isViewer={isViewer}
      />

      {/* Add asset panel */}
      {showAddAsset && (
        <AddAssetPanel
          jobId={jobId}
          taskId={taskId}
          open={showAddAsset}
          onClose={() => setShowAddAsset(false)}
          onCreated={(id) => {
            setShowAddAsset(false);
            setSelectedEntryId(id);
          }}
        />
      )}

      {/* Bulk action bar (checkbox selection) */}
      {selectionEnabled && (
        <BulkActionBar
          count={selectedIds.size}
          onDone={() => setSelectedIds(new Set())}
        >
          <BulkActionButton onClick={() => setBulkValuationOpen(true)}>
            Update valuations
          </BulkActionButton>
          <BulkActionButton
            onClick={handleBulkExclude}
            tone={bulkExcludeIntent ? "danger" : "default"}
          >
            {bulkExcludeIntent ? "Exclude" : "Re-include"}
          </BulkActionButton>
        </BulkActionBar>
      )}

      <BulkValuationDialog
        open={bulkValuationOpen}
        onOpenChange={setBulkValuationOpen}
        count={selectedIds.size}
        title="Update valuations"
        itemNoun="asset"
        amountLabel="Valuation ($)"
        submitting={bulkSubmitting}
        onSubmit={handleBulkValuation}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group header row
// ---------------------------------------------------------------------------

function GroupHeaderRow({
  group,
  fixedColCount,
  isExpanded,
  onToggle,
}: {
  group: AssetGroup;
  fixedColCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow
      className="cursor-pointer select-none border-t border-border bg-muted/40 hover:bg-muted/50"
      onClick={onToggle}
    >
      <TableCell colSpan={fixedColCount} className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <Badge
            color={
              group.key === "secured"
                ? "amber"
                : group.key === "unsecured"
                  ? "gray"
                  : ASSET_TYPE_BADGE_COLOR[group.key ?? ""]
            }
            variant="soft"
            size="sm"
          >
            {group.label}
          </Badge>
          <span className="text-xs font-medium text-muted-foreground">
            {group.entries.length} asset
            {group.entries.length !== 1 ? "s" : ""}
          </span>
        </div>
      </TableCell>
      <TableCell className="px-4 py-2.5 text-right">
        <span className="text-sm font-bold tabular-nums text-foreground">
          {formatFullCurrency(group.totals.rocap)}
        </span>
      </TableCell>
      <TableCell className="px-4 py-2.5 text-right">
        <span className="text-sm font-bold tabular-nums text-foreground">
          {formatFullCurrency(group.totals.practitioner)}
        </span>
      </TableCell>
      <TableCell className="px-4 py-2.5 text-right">
        <span className="text-sm font-bold tabular-nums text-foreground">
          {formatFullCurrency(group.totals.low)}
        </span>
      </TableCell>
      <TableCell className="px-4 py-2.5 text-right">
        <span className="text-sm font-bold tabular-nums text-foreground">
          {formatFullCurrency(group.totals.high)}
        </span>
      </TableCell>
      {group.valuationTotals.map((total, i) => (
        <TableCell key={i} className="px-4 py-2.5 text-right">
          <span className="text-sm font-bold tabular-nums text-foreground">
            {formatFullCurrency(total)}
          </span>
        </TableCell>
      ))}
      <TableCell />
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Entry row
// ---------------------------------------------------------------------------

function EntryRow({
  entry,
  isSelected,
  isGrouped,
  onSelect,
  selectionEnabled,
  isChecked,
  onToggleCheck,
  flagCtx,
  linkCount,
  rocapValue,
  practitionerValue,
  lowValue,
  highValue,
  valuationValues,
  securedBalance,
  linkStatus,
  isNew,
}: {
  entry: AssetEntryUI;
  isSelected: boolean;
  isGrouped: boolean;
  onSelect: () => void;
  /** Whether the leading checkbox column is shown (hidden for viewers). */
  selectionEnabled: boolean;
  /** Whether this row is part of the current bulk selection. */
  isChecked: boolean;
  /** Toggle this row's membership in the bulk selection. */
  onToggleCheck: () => void;
  flagCtx: UseFlagDismissalsResult;
  linkCount: number;
  /** Column values (already gross or net-of-security per the active toggle). */
  rocapValue: number | null;
  practitionerValue: number | null;
  lowValue: number | null;
  highValue: number | null;
  /** Per-visible-source values, ordered like the headers; gross or net per the toggle. */
  valuationValues: (number | null)[];
  /** Secured balance registered against this asset (undefined when none). */
  securedBalance?: number;
  /** Why the secured net value can't be computed yet (undefined when fine). */
  linkStatus?: AssetSecurityLinkStatus;
  /** Whether this asset was created since the user last viewed the agent. */
  isNew: boolean;
}) {
  const isRecoveryOverride = entry.estimatedRecovery?.isOverride === true;
  const excluded = entry.isExcluded;
  const allFlags = entry.validationFlags ?? [];
  // Only flags that haven't been dismissed count toward the badges + glow.
  const flags = allFlags.filter(
    (f) =>
      !flagCtx.isDismissed("assetList", entry._id, {
        type: f.type,
        detail: f.reason,
      }),
  );
  const uniqueWarnings = new Set(
    flags.filter((f) => f.severity === "warning").map((f) => f.type),
  );
  const uniqueInfos = new Set(
    flags.filter((f) => f.severity === "info").map((f) => f.type),
  );

  return (
    <TableRow
      className={cn(
        "cursor-pointer transition-colors",
        isSelected ? "hover:bg-primary/8 bg-primary/5" : "hover:bg-muted/50",
        isGrouped && "pl-2",
      )}
      onClick={onSelect}
    >
      {/* Description */}
      <TableCell className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          {selectionEnabled && (
            <span
              onClick={(e) => e.stopPropagation()}
              className="flex items-center"
            >
              <Checkbox
                aria-label={`Select ${entry.description}`}
                checked={isChecked}
                onCheckedChange={onToggleCheck}
              />
            </span>
          )}
          <span
            className={cn(
              "line-clamp-1 text-sm font-medium",
              excluded
                ? "text-muted-foreground line-through"
                : "text-foreground",
            )}
          >
            {entry.description}
          </span>
          {isNew && <NewOutputBadge />}
          <LinkIndicator count={linkCount} />
        </div>
      </TableCell>

      {/* Type (hidden when grouped — shown in group header) */}
      {!isGrouped && (
        <TableCell className="px-4 py-3">
          <Badge
            color={ASSET_TYPE_BADGE_COLOR[entry.assetType]}
            variant="soft"
            size="sm"
            className="whitespace-nowrap"
          >
            {ASSET_TYPE_LABEL[entry.assetType] ?? entry.assetType}
          </Badge>
        </TableCell>
      )}

      {/* Status */}
      <TableCell className="px-4 py-3">
        {excluded ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
            <EyeOff className="h-3 w-3" />
            Excluded
          </span>
        ) : (
          <span
            className={cn(
              "text-xs font-semibold",
              entry.assetStatus === "Identified"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400",
            )}
          >
            {entry.assetStatus}
          </span>
        )}
      </TableCell>

      {/* Secured / Unsecured — the registered security amount is shown inline
          in the badge when known. A red broken-link badge flags a secured asset
          that can't be netted yet because a link is missing. */}
      <TableCell className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge
            color={entry.subjectToSecurityInterest ? "amber" : "gray"}
            variant="soft"
            size="sm"
            className="whitespace-nowrap"
          >
            {entry.subjectToSecurityInterest
              ? securedBalance != null && securedBalance > 0
                ? `Secured · $${Math.min(
                    securedBalance,
                    getSelectedValue(entry) ?? getBestValue(entry),
                  ).toLocaleString("en-AU", {
                    maximumFractionDigits: 0,
                  })}`
                : "Secured"
              : "Unsecured"}
          </Badge>
          {linkStatus && (
            <Badge
              color="red"
              variant="soft"
              size="sm"
              className="gap-0.5 whitespace-nowrap"
              title={SECURITY_LINK_WARNING[linkStatus].full}
            >
              <Unlink className="h-3 w-3" />
              {SECURITY_LINK_WARNING[linkStatus].short}
            </Badge>
          )}
        </div>
      </TableCell>

      {/* Flags */}
      <TableCell className="px-4 py-3">
        {flags.length > 0 && (
          <div className="flex items-center gap-1">
            {uniqueWarnings.size > 0 && (
              <Badge
                color="red"
                variant="soft"
                size="sm"
                className={cn("gap-0.5", flagGlowClass(true))}
              >
                <AlertTriangle className="h-3 w-3" />
                {uniqueWarnings.size}
              </Badge>
            )}
            {uniqueInfos.size > 0 && (
              <Badge
                color="sky"
                variant="soft"
                size="sm"
                className={flagGlowClass(true)}
              >
                {uniqueInfos.size}
              </Badge>
            )}
          </div>
        )}
      </TableCell>

      {/* ROCAP value */}
      <TableCell className="px-4 py-3 text-right">
        <div
          className={cn(
            "text-sm font-semibold tabular-nums",
            rocapValue == null
              ? "text-muted-foreground/40"
              : excluded
                ? "text-muted-foreground line-through"
                : "text-foreground",
          )}
        >
          {formatFullCurrency(rocapValue)}
        </div>
      </TableCell>

      {/* Practitioner valuation */}
      <TableCell className="px-4 py-3 text-right">
        <div
          className={cn(
            "text-sm font-semibold tabular-nums",
            practitionerValue == null
              ? "text-muted-foreground/40"
              : excluded
                ? "text-muted-foreground line-through"
                : "text-foreground",
          )}
        >
          {formatFullCurrency(practitionerValue)}
        </div>
      </TableCell>

      {/* Low estimated recovery */}
      <TableCell className="px-4 py-3 text-right">
        <div
          className={cn(
            "text-sm font-semibold tabular-nums",
            lowValue == null
              ? "text-muted-foreground/40"
              : excluded
                ? "text-muted-foreground line-through"
                : "text-foreground",
          )}
        >
          {formatFullCurrency(lowValue)}
        </div>
        {isRecoveryOverride && lowValue != null && (
          <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
            Override
          </div>
        )}
      </TableCell>

      {/* High estimated recovery */}
      <TableCell className="px-4 py-3 text-right">
        <div
          className={cn(
            "text-sm font-semibold tabular-nums",
            highValue == null
              ? "text-muted-foreground/40"
              : excluded
                ? "text-muted-foreground line-through"
                : "text-foreground",
          )}
        >
          {formatFullCurrency(highValue)}
        </div>
        {isRecoveryOverride && highValue != null && (
          <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
            Override
          </div>
        )}
      </TableCell>

      {/* Valuation-source columns (MOR-544) — em dash when the asset has no
          valuation from that source. */}
      {valuationValues.map((value, i) => (
        <TableCell key={i} className="px-4 py-3 text-right">
          <div
            className={cn(
              "text-sm font-semibold tabular-nums",
              value == null
                ? "text-muted-foreground/40"
                : excluded
                  ? "text-muted-foreground line-through"
                  : "text-foreground",
            )}
          >
            {formatFullCurrency(value)}
          </div>
        </TableCell>
      ))}

      {/* Chevron */}
      <TableCell className="px-4 py-3">
        <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
      </TableCell>
    </TableRow>
  );
}
